import { useEffect, useRef, useState, memo } from 'react';
import './LandingPage.css';
import { CONFIGS, SECURITY_DEPOSIT } from './config';

const HERO_DUR = 7800;
const REVIEW_DUR = 5000;
// Matches config.js's CONFIGS.gold/silver (single source of truth for real
// facility numbers) — the two stats that actually differ between units when
// the hero tour switches. Generator (24hr) and distance (10min) are
// identical for both, so those two stay put.
const HERO_STATS = {
  gold: {
    pool: 60, poolLabel: 'Longest pool, 6 ft deep',
    rooms: 4, roomsLabel: 'Non-stop ACs & furnished rooms',
    lede: "A 60-foot swimming pool, lush cricket lawns, BBQ & bonfire nights, indoor games and fully-furnished rooms — 10 minutes from Gulshan-e-Maymar. Two farmhouses. One unforgettable day out with friends & family.",
  },
  silver: {
    pool: 50, poolLabel: 'Pool length, 6 ft deep',
    rooms: 2, roomsLabel: 'Rooms with attached bathrooms',
    lede: "A 50-foot swimming pool, a big green lawn, BBQ & bonfire nights, indoor games and 2 comfortable rooms — 10 minutes from Gulshan-e-Maymar. Two farmhouses. One unforgettable day out with friends & family.",
  },
};

const ICON_MUTED = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>';
const ICON_UNMUTED = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path></svg>';

// Ported from the Claude Design export's js/main.js. Everything here is
// vanilla DOM/CSS-driven (IntersectionObserver + CSS keyframes/transitions
// already carried over in LandingPage.css) — no animation library needed.
// Uses refs instead of document.getElementById so React StrictMode's
// dev-mode double-invoke can't double-attach listeners/observers/intervals;
// every listener/observer/interval is torn down in the cleanup function.
function useLandingPageEffects(refs) {
  useEffect(() => {
    const cleanups = [];

    // App.css (loaded in the same bundle regardless of which page is
    // showing, since Root imports both App and LandingPage statically) sets
    // `body { padding: 20px }` for the booking flow's own layout. That's a
    // real ancestor of .rf-page, so it was framing the whole landing page in
    // a 20px gutter — most noticeable on small phone screens. Override it
    // only while this component is mounted, and hand it back on unmount so
    // the booking flow's own padding is untouched when someone follows a
    // #gold/#silver link and Root swaps LandingPage out for App.
    const prevBodyPadding = document.body.style.padding;
    document.body.style.padding = '0';
    cleanups.push(() => { document.body.style.padding = prevBodyPadding; });

    // ---- nav shadow on scroll (rAF-throttled) ----
    const nav = refs.nav.current;
    let navScrolled = null, navTicking = false;
    const onScroll = () => {
      if (navTicking) return;
      navTicking = true;
      requestAnimationFrame(() => {
        const scrolled = (window.scrollY || 0) > 40;
        if (nav && scrolled !== navScrolled) {
          navScrolled = scrolled;
          nav.classList.toggle('rf-nav--scrolled', scrolled);
        }
        navTicking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));

    // ---- scroll reveal + stat counters ----
    const revealItems = refs.page.current.querySelectorAll('[data-reveal]');
    const runCounters = (scope) => {
      scope.querySelectorAll('[data-count]').forEach((el) => {
        let target = parseFloat(el.getAttribute('data-count')) || 0;
        // Two of these spans are dynamic (see the hero wipe slider below) —
        // if the visitor already switched to Silver before scrolling this
        // far, the first reveal should count up to Silver's numbers, not
        // silently reset back to Gold's static data-count default.
        if (el === refs.statPoolCount.current) target = (heroTab === 0 ? HERO_STATS.gold : HERO_STATS.silver).pool;
        if (el === refs.statRoomsCount.current) target = (heroTab === 0 ? HERO_STATS.gold : HERO_STATS.silver).rooms;
        const dur = 1400, start = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased).toString();
          if (p < 1) requestAnimationFrame(step);
          else el.textContent = target.toString();
        };
        requestAnimationFrame(step);
      });
    };
    const revealIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.classList.add('is-visible');
          if (el.querySelector('[data-count]')) runCounters(el);
          revealIO.unobserve(el);
        }
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
    revealItems.forEach((el) => revealIO.observe(el));
    cleanups.push(() => revealIO.disconnect());

    // ---- hero Gold/Silver wipe slider ----
    const silverBg = refs.silverBg.current;
    const wipeEdge = refs.wipeEdge.current;
    const goldTab = refs.goldTab.current;
    const silverTab = refs.silverTab.current;
    const tourLabel = refs.tourLabel.current;
    const progress = refs.tourProgress.current;
    const statPoolCount = refs.statPoolCount.current;
    const statPoolLabel = refs.statPoolLabel.current;
    const statRoomsCount = refs.statRoomsCount.current;
    const statRoomsLabel = refs.statRoomsLabel.current;
    const heroLede = refs.heroLede.current;
    let heroTab = 0, heroTimer = null;
    const WIPE_DUR = 1.75; // seconds — was 1.15s, slowed down so the switch reads clearly

    function runProgress(dur) {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.width = '0%';
      void progress.offsetWidth; // force reflow so the reset applies first
      progress.style.transition = 'width ' + dur + 'ms linear';
      progress.style.width = '100%';
    }

    // Counts up/down from whatever's currently shown to the new target,
    // rather than restarting from 0 — reads as an update, not a reset.
    function animateStatTo(el, target, dur = 1000) {
      if (!el) return;
      const start = parseFloat(el.textContent) || 0;
      if (start === target) return;
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(start + (target - start) * eased).toString();
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = target.toString();
      };
      requestAnimationFrame(step);
    }

    function setHero(i, userInitiated) {
      if (heroTab === i && !userInitiated) return;
      heroTab = i;
      const gold = i === 0;
      const stats = gold ? HERO_STATS.gold : HERO_STATS.silver;

      if (silverBg) silverBg.style.clipPath = gold ? 'inset(0 0 0 100%)' : 'inset(0)';

      if (wipeEdge) {
        wipeEdge.style.transition = 'none';
        wipeEdge.style.right = gold ? '0' : '100%';
        void wipeEdge.offsetWidth;
        wipeEdge.style.opacity = '1';
        wipeEdge.style.transition = `right ${WIPE_DUR}s cubic-bezier(.76,0,.24,1), opacity .6s ease ${WIPE_DUR - 0.3}s`;
        wipeEdge.style.right = gold ? '100%' : '0';
        wipeEdge.style.opacity = '0';
      }

      if (goldTab && silverTab) {
        goldTab.classList.toggle('is-active', gold);
        silverTab.classList.toggle('is-active', !gold);
      }
      if (tourLabel) tourLabel.textContent = gold ? 'Now viewing · Gold' : 'Now viewing · Silver';

      // Stat band updates in sync with the picture — pool size and
      // ACs/rooms actually differ between the two units (see HERO_STATS,
      // sourced from config.js); generator and distance stay the same.
      animateStatTo(statPoolCount, stats.pool);
      if (statPoolLabel) statPoolLabel.textContent = stats.poolLabel;
      animateStatTo(statRoomsCount, stats.rooms);
      if (statRoomsLabel) statRoomsLabel.textContent = stats.roomsLabel;
      if (heroLede) heroLede.textContent = stats.lede;

      if (userInitiated) startHeroTimer();
    }

    function startHeroTimer() {
      runProgress(HERO_DUR);
      if (heroTimer) clearInterval(heroTimer);
      heroTimer = setInterval(() => setHero(heroTab ? 0 : 1, false), HERO_DUR);
    }

    const onGoldTab = () => setHero(0, true);
    const onSilverTab = () => setHero(1, true);
    if (goldTab) goldTab.addEventListener('click', onGoldTab);
    if (silverTab) silverTab.addEventListener('click', onSilverTab);
    startHeroTimer();
    cleanups.push(() => {
      if (goldTab) goldTab.removeEventListener('click', onGoldTab);
      if (silverTab) silverTab.removeEventListener('click', onSilverTab);
      if (heroTimer) clearInterval(heroTimer);
    });

    // ---- walk-through video: same UX as the review video below — deferred
    // load, autoplay muted once scrolled into view, pauses when scrolled
    // away, custom mute toggle instead of the native browser control bar.
    const walkthroughSection = refs.walkthroughSection.current;
    const walkthroughVideo = refs.walkthroughVideo.current;
    const walkthroughSpinner = refs.walkthroughSpinner.current;
    const walkthroughMuteBtn = refs.walkthroughMuteBtn.current;
    const walkthroughMuteIcon = refs.walkthroughMuteIcon.current;

    if (walkthroughVideo && walkthroughSection) {
      const wtIO = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (!walkthroughVideo.src && walkthroughVideo.dataset.src) {
            walkthroughVideo.src = walkthroughVideo.dataset.src;
            walkthroughVideo.muted = true;
            walkthroughVideo.addEventListener('playing', () => { if (walkthroughSpinner) walkthroughSpinner.style.display = 'none'; }, { once: true });
          }
          walkthroughVideo.play().catch(() => {});
        } else {
          walkthroughVideo.pause();
        }
      }, { threshold: 0.2, rootMargin: '200px 0px' });
      wtIO.observe(walkthroughSection);
      cleanups.push(() => wtIO.disconnect());
    }

    const onWalkthroughMuteClick = () => {
      if (!walkthroughVideo) return;
      walkthroughVideo.muted = !walkthroughVideo.muted;
      if (!walkthroughVideo.muted) { walkthroughVideo.volume = 1; walkthroughVideo.play().catch(() => {}); }
      if (walkthroughMuteIcon) walkthroughMuteIcon.innerHTML = walkthroughVideo.muted ? ICON_MUTED : ICON_UNMUTED;
    };
    if (walkthroughMuteBtn && walkthroughVideo) {
      walkthroughMuteBtn.addEventListener('click', onWalkthroughMuteClick);
      cleanups.push(() => walkthroughMuteBtn.removeEventListener('click', onWalkthroughMuteClick));
    }

    // ---- floating pool bubbles (only while the pool section is visible) ----
    const pool = refs.pool.current;
    const bubbleLeft = refs.bubbleLeft.current;
    const bubbleRight = refs.bubbleRight.current;
    const bubbleContainers = [bubbleLeft, bubbleRight].filter(Boolean);
    if (pool && bubbleContainers.length) {
      let bubbleTimer = null, bubbleRunning = false;
      const makeBubble = (container) => {
        const b = document.createElement('span');
        const size = 7 + Math.random() * 15;
        b.className = 'rf-bubble';
        b.style.left = (Math.random() * 88) + '%';
        b.style.width = size + 'px';
        b.style.height = size + 'px';
        b.style.animationDuration = (8 + Math.random() * 5) + 's';
        container.appendChild(b);
        setTimeout(() => b.remove(), 13000);
      };
      const bubbleIO = new IntersectionObserver((entries) => {
        const visible = entries[0].isIntersecting;
        if (visible && !bubbleRunning) {
          bubbleRunning = true;
          bubbleTimer = setInterval(() => bubbleContainers.forEach(makeBubble), 1400);
        } else if (!visible && bubbleRunning) {
          bubbleRunning = false;
          clearInterval(bubbleTimer);
        }
      }, { threshold: 0.05 });
      bubbleIO.observe(pool);
      cleanups.push(() => { bubbleIO.disconnect(); if (bubbleTimer) clearInterval(bubbleTimer); });
    }

    // ---- reviews: video (deferred load, mute toggle, visibility play/pause) ----
    const reviewsSection = refs.reviewsSection.current;
    const video = refs.video.current;
    const vidSpinner = refs.vidSpinner.current;
    const muteBtn = refs.muteBtn.current;
    const muteIcon = refs.muteIcon.current;

    if (video && reviewsSection) {
      const vidIO = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.muted = true;
            video.addEventListener('playing', () => { if (vidSpinner) vidSpinner.style.display = 'none'; }, { once: true });
          }
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }, { threshold: 0.2, rootMargin: '200px 0px' });
      vidIO.observe(reviewsSection);
      cleanups.push(() => vidIO.disconnect());
    }

    const onMuteClick = () => {
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted) { video.volume = 1; video.play().catch(() => {}); }
      if (muteIcon) muteIcon.innerHTML = video.muted ? ICON_MUTED : ICON_UNMUTED;
    };
    if (muteBtn && video) {
      muteBtn.addEventListener('click', onMuteClick);
      cleanups.push(() => muteBtn.removeEventListener('click', onMuteClick));
    }

    // ---- reviews: auto-switching text carousel ----
    const reviewCards = Array.from(refs.reviewStack.current ? refs.reviewStack.current.querySelectorAll('[data-review-card]') : []);
    const reviewDots = Array.from(refs.reviewDots.current ? refs.reviewDots.current.querySelectorAll('[data-review-dot]') : []);
    let reviewIndex = 0, reviewTimer = null;

    function showReview(i, userInitiated) {
      reviewIndex = i;
      reviewCards.forEach((c, idx) => c.classList.toggle('is-active', idx === i));
      reviewDots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      if (userInitiated) restartReviewTimer();
    }
    function restartReviewTimer() {
      if (reviewTimer) clearInterval(reviewTimer);
      reviewTimer = setInterval(() => showReview((reviewIndex + 1) % reviewCards.length, false), REVIEW_DUR);
    }
    const dotHandlers = [];
    if (reviewCards.length) {
      reviewDots.forEach((d, i) => {
        const handler = () => showReview(i, true);
        d.addEventListener('click', handler);
        dotHandlers.push([d, handler]);
      });
      showReview(0, false);
      restartReviewTimer();
    }
    cleanups.push(() => {
      dotHandlers.forEach(([d, handler]) => d.removeEventListener('click', handler));
      if (reviewTimer) clearInterval(reviewTimer);
    });

    return () => cleanups.forEach((fn) => fn());
    // Intentionally empty deps: `refs` is a fresh object literal every render
    // (each individual useRef().current is stable, but the wrapping object
    // isn't), and this effect must behave like main.js's original one-time
    // IIFE — run once on mount, not on every re-render Root's hashchange
    // listener could trigger while still showing this same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function CheckIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
      <path d="M1.5 5.2L4 7.5L8.5 2.5" stroke="#0d3b37" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Wrapped in memo() and never given props on purpose: every interactive
// behavior after mount (hero wipe slider, nav scroll shadow, review
// carousel, etc.) mutates the DOM directly via refs, outside React state.
// If this component ever re-rendered (e.g. Root re-rendering on a
// hashchange while still showing the landing page), React's reconciliation
// would reset className/style back to their static JSX values and silently
// undo all of that.
function LandingPage() {
  const [navOpen, setNavOpen] = useState(false);
  const refs = {
    page: useRef(null),
    nav: useRef(null),
    goldBg: useRef(null),
    silverBg: useRef(null),
    wipeEdge: useRef(null),
    goldTab: useRef(null),
    silverTab: useRef(null),
    tourLabel: useRef(null),
    tourProgress: useRef(null),
    statPoolCount: useRef(null),
    statPoolLabel: useRef(null),
    statRoomsCount: useRef(null),
    statRoomsLabel: useRef(null),
    heroLede: useRef(null),
    walkthroughSection: useRef(null),
    walkthroughVideo: useRef(null),
    walkthroughSpinner: useRef(null),
    walkthroughMuteBtn: useRef(null),
    walkthroughMuteIcon: useRef(null),
    pool: useRef(null),
    bubbleLeft: useRef(null),
    bubbleRight: useRef(null),
    reviewsSection: useRef(null),
    video: useRef(null),
    vidSpinner: useRef(null),
    muteBtn: useRef(null),
    muteIcon: useRef(null),
    reviewStack: useRef(null),
    reviewDots: useRef(null),
  };

  useLandingPageEffects(refs);

  const goldFacilities = [
    'Biggest swimming pool 30×60 ft, 6 ft deep',
    'BBQ area + bonfire area with seating',
    'Big lush garden for cricket & football',
    '3 bedrooms with attached bathrooms',
    'Indoor games: Dabbo, Patti',
    'Big lounge with sofas & deep freezer',
    '24-hr generator + furnished kitchen (gas stove)',
    'Indoor / outdoor dining + Masjid facility',
    '3 washrooms, changing & shower area by the pool',
    'Swings, see-saw & benches — safe for families',
  ];
  // Sourced straight from config.js's CONFIGS — the exact numbers (and
  // weekend day-rules) the real booking calculator uses, so this marketing
  // copy can never quietly drift out of sync with what customers actually
  // get charged. Note the two units' weekends aren't the same days: Gold's
  // isWeekend is Fri–Sat, Silver's is Fri–Sun (Sunday counts as a Silver
  // weekend but a Gold weekday) — labeled per-unit below rather than
  // assuming both match.
  const pricing = {
    gold: {
      slots10: [
        { period: 'Weekday · Sun–Thu', rows: [
          { label: '☀️ Morning · 8AM–6PM', price: CONFIGS.gold.prices.dayWeekday },
          { label: '🌙 Night · 8PM–6AM', price: CONFIGS.gold.prices.nightWeekday },
        ] },
        { period: 'Weekend · Fri–Sat + Public Holidays', rows: [
          { label: '☀️🌙 Morning or Night', price: CONFIGS.gold.prices.dayWeekend },
        ] },
      ],
      slot22: [
        { label: 'Weekday', price: CONFIGS.gold.prices.fullWeekday },
        { label: 'Weekend', price: CONFIGS.gold.prices.fullWeekend },
      ],
    },
    silver: {
      slots10: [
        { period: 'Weekday · Mon–Thu', rows: [
          { label: '☀️ Day · 8AM–6PM', price: CONFIGS.silver.prices.dayWeekday },
          { label: '🌙 Night · 8PM–6AM', price: CONFIGS.silver.prices.nightWeekday },
        ] },
        { period: 'Weekend · Fri–Sun + Public Holidays', rows: [
          { label: '☀️🌙 Day / Night', price: CONFIGS.silver.prices.dayWeekend },
        ] },
      ],
      slot22: [
        { label: 'Weekday', price: CONFIGS.silver.prices.fullWeekday },
        { label: 'Weekend', price: CONFIGS.silver.prices.fullWeekend },
      ],
    },
  };
  const silverFacilities = [
    'Swimming pool 30×50 ft, 6 ft deep',
    'BBQ area + bonfire area with seating',
    'Big lush green garden for cricket & football',
    '2 rooms with 2 attached baths',
    'Indoor games: Dabbo, Patti, Foosball',
    'Deep freezer + 24-hr generator',
    'Furnished kitchen with gas cylinder & stove',
    'Indoor & outdoor dining + Masjid facility',
    '2 washrooms, changing & shower area with pool',
    'Sitting areas & benches — safe & secure for families',
  ];
  const reviews = [
    { initial: 'B', name: 'Bilal Ahmed', quote: 'Best farmhouse experience in Karachi. The pool is huge and spotless, and the staff took great care of our whole family.' },
    { initial: 'S', name: 'Sana Khan', quote: 'Booked the Gold for a birthday. Lush lawn, great BBQ area and the rooms had non-stop AC. Kids loved the swings.' },
    { initial: 'U', name: 'Usman Malik', quote: 'Clean, secure and very peaceful. 10 minutes from Gulshan-e-Maymar. Will definitely come back with friends.' },
    { initial: 'H', name: 'Hira Siddiqui', quote: 'Amazing value for a full-day slot. The bonfire at night and the big pool made it a perfect family day out.' },
  ];
  // Grouped so each catalog reads as "Poolside", "Rooms", "Outdoor & Lawn",
  // "Common Areas" instead of one undifferentiated grid — every photo is
  // already a uniform 9:16 crop (see public/landing/gold|silver), so a
  // plain equal-cell grid works cleanly within each group.
  const goldGalleryGroups = [
    {
      label: '🏊 Poolside', items: [
        { src: '/landing/gold/gold-pool-night.jpg', alt: 'Poolside at night with games set up · Gold', caption: 'Poolside · Night' },
      ],
    },
    {
      label: '🛏️ Rooms', items: [
        { src: '/landing/gold/gold-bedroom-1.jpg', alt: 'Furnished bedroom · Gold', caption: 'Bedroom' },
        { src: '/landing/gold/gold-bedroom-2.jpg', alt: 'Furnished bedroom · Gold', caption: 'Bedroom' },
        { src: '/landing/gold/gold-desi-bethak.jpg', alt: 'Desi-style bethak · Gold', caption: 'Desi-style bethak' },
      ],
    },
    {
      label: '🌳 Outdoor & Lawn', items: [
        { src: '/landing/gold/gold-aerial-lawn.jpg', alt: 'Aerial view of the gazebo & cricket lawn · Gold', caption: 'Gazebo & cricket lawn' },
        { src: '/landing/gold/gold-gazebo-sunset.jpg', alt: 'Gazebo seating at sunset · Gold', caption: 'Gazebo · Sunset' },
        { src: '/landing/gold/gold-lawn-dining.jpg', alt: 'Outdoor dining area on the lawn · Gold', caption: 'Outdoor dining' },
        { src: '/landing/gold/gold-house-sunset.jpg', alt: 'Farmhouse exterior at sunset · Gold', caption: 'Farmhouse at sunset' },
      ],
    },
    {
      label: '🛋️ Common Areas', items: [
        { src: '/landing/gold/gold-lounge.jpg', alt: 'Grand lounge with hexagon ceiling · Gold', caption: 'Grand lounge' },
        { src: '/landing/gold/gold-games-room.jpg', alt: 'Indoor games room with carrom & foosball · Gold', caption: 'Indoor games room' },
      ],
    },
  ];
  const silverGalleryGroups = [
    {
      label: '🏊 Poolside', items: [
        { src: '/landing/silver/silver-pool-dark.jpg', alt: 'Dark-tiled pool at night · Silver', caption: 'Pool · Night' },
        { src: '/landing/silver/silver-pool-rain.jpg', alt: 'Poolside in the rain · Silver', caption: 'Poolside · Rain' },
      ],
    },
    {
      label: '🛏️ Rooms', items: [
        { src: '/landing/silver/silver-bedroom.jpg', alt: 'Furnished bedroom · Silver', caption: 'Furnished bedroom' },
        { src: '/landing/silver/silver-desi-bethak.jpg', alt: 'Desi-style bethak · Silver', caption: 'Desi-style bethak' },
      ],
    },
    {
      label: '🌳 Outdoor & Lawn', items: [
        { src: '/landing/silver/silver-garden-sunset.jpg', alt: 'Garden at sunset · Silver', caption: 'Garden · Sunset' },
        { src: '/landing/silver/silver-playarea.jpg', alt: "Kids' play area · Silver", caption: "Kids' play area" },
      ],
    },
  ];

  return (
    <div className="rf-page" id="top" ref={refs.page}>

      {/* ===== PROMO MARQUEE BAR ===== */}
      <div className="rf-marquee">
        <div className="rf-marquee__track">
          <span className="rf-marquee__item">🔥 Book your slot now &nbsp;·&nbsp; Use code RIAZFARMHOUSENOW → 10,000 PKR off Gold &nbsp;·&nbsp; Use code RIAZSILVERNOW → 5,000 PKR off Silver &nbsp;·&nbsp; Karachi's biggest private pool &nbsp;·</span>
          <span className="rf-marquee__item" aria-hidden="true">🔥 Book your slot now &nbsp;·&nbsp; Use code RIAZFARMHOUSENOW → 10,000 PKR off Gold &nbsp;·&nbsp; Use code RIAZSILVERNOW → 5,000 PKR off Silver &nbsp;·&nbsp; Karachi's biggest private pool &nbsp;·</span>
        </div>
      </div>

      {/* ===== NAV ===== */}
      <nav className={`rf-nav ${navOpen ? 'rf-nav--menu-open' : ''}`} ref={refs.nav}>
        <a href="#top" className="rf-nav__brand" onClick={() => setNavOpen(false)}>
          <img loading="lazy" decoding="async" src="/logo.png" alt="Riaz Farmhouse" className="rf-nav__logo" />
          <span className="rf-nav__wordmark">Riaz Farmhouse</span>
        </a>
        <div className="rf-nav__right">
          <div className="rf-nav__links">
            <a href="#stays" onClick={() => setNavOpen(false)}>Farmhouses</a>
            <a href="#pricing" onClick={() => setNavOpen(false)}>Pricing</a>
            <a href="#pool" onClick={() => setNavOpen(false)}>The Pool</a>
            <a href="#gallery" onClick={() => setNavOpen(false)}>Gallery</a>
            <a href="#walkthrough" onClick={() => setNavOpen(false)}>Walk-Through</a>
            <a href="#reviews" onClick={() => setNavOpen(false)}>Reviews</a>
            <a href="#location" onClick={() => setNavOpen(false)}>Location</a>
          </div>
          <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '5.2s' }}>Book Now →</a>
          <a href="http://wa.me/+923392959290" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--whatsapp" aria-label="Chat with us on WhatsApp">
            <span className="rf-btn__wa-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20.52 3.48C18.39 1.35 15.38 0 12 0 5.37 0 0 5.37 0 12c0 2.1.55 4.15 1.59 5.95L0 24l6.27-1.64A11.92 11.92 0 0012 24c6.63 0 12-5.37 12-12 0-3.38-1.35-6.39-3.48-8.52z" fill="#25D366"/><path d="M17.55 14.63c-.29-.14-1.73-.86-2-.96-.27-.1-.47-.14-.67.14-.19.29-.74.96-.91 1.16-.17.19-.33.21-.62.07-.29-.14-1.22-.45-2.33-1.44-.86-.76-1.44-1.7-1.61-1.99-.17-.29-.02-.44.12-.58.12-.12.27-.32.4-.48.13-.16.17-.28.26-.46.09-.19.05-.35-.02-.49-.07-.14-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.19 0-.5.07-.77.35-.27.28-1.03 1.01-1.03 2.47s1.06 2.86 1.21 3.06c.14.19 2.09 3.18 5.07 4.46.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.09 1.73-.7 1.98-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.19-.57-.33z" fill="#FFF"/></svg>
            </span>
            <span className="rf-btn__wa-label">WhatsApp</span>
          </a>
          <button
            type="button"
            className="rf-nav__burger"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
            onClick={() => setNavOpen(o => !o)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <header className="rf-hero">
        <div className="rf-hero__bgwrap">
          <div className="rf-hero__bg rf-hero__bg--gold" ref={refs.goldBg} style={{ backgroundImage: "url(/landing/aerial.jpg)" }}></div>
          <div className="rf-hero__bg rf-hero__bg--silver" ref={refs.silverBg} style={{ backgroundImage: "url(/landing/silver-lawn.jpg)" }}></div>
          <div className="rf-hero__edge" ref={refs.wipeEdge}></div>
        </div>
        <div className="rf-hero__scrim"></div>
        <div className="rf-hero__content">
          <div className="rf-badge rf-hero__line" style={{ animationDelay: '.05s' }}>
            <span className="rf-badge__dot"></span>Located in Dua Greens · Karachi · Since 2024
          </div>
          <p className="rf-hero__welcome rf-hero__line" style={{ animationDelay: '.12s' }}>Welcome to Riaz Farmhouse</p>
          <h1 className="rf-hero__title">
            <span className="rf-hero__line" style={{ animationDelay: '.18s' }}>One of the biggest</span>
            <span className="rf-hero__line rf-hero__title-accent" style={{ animationDelay: '.34s' }}>private-pool farmhouses</span>
            <span className="rf-hero__line" style={{ animationDelay: '.5s' }}>in Karachi.</span>
          </h1>
          <p className="rf-hero__lede rf-hero__line" style={{ animationDelay: '.66s' }} ref={refs.heroLede}>
            A 60-foot swimming pool, lush cricket lawns, BBQ &amp; bonfire nights, indoor games and fully-furnished rooms — 10 minutes from Gulshan-e-Maymar. Two farmhouses. One unforgettable day out with friends &amp; family.
          </p>
          <div className="rf-hero__ctas rf-hero__line" style={{ animationDelay: '.82s' }}>
            <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '4.8s' }}>Book your slot →</a>
            <a href="#stays" className="rf-btn rf-btn--ghost-cream">See both farmhouses</a>
          </div>
        </div>

        {/* Gold / Silver wipe slider control */}
        <div className="rf-tour">
          <div className="rf-tour__label"><span ref={refs.tourLabel}>Now viewing · Gold</span></div>
          <div className="rf-tour__tabs">
            <button className="rf-tour__tab is-active" ref={refs.goldTab}>Gold</button>
            <button className="rf-tour__tab" ref={refs.silverTab}>Silver</button>
          </div>
          <div className="rf-tour__track"><div className="rf-tour__progress" ref={refs.tourProgress}></div></div>
        </div>

        <div className="rf-scrollcue">
          <span>Scroll</span>
          <span></span>
        </div>
      </header>

      {/* ===== USP STAT BAND ===== */}
      <section className="rf-stats">
        <div className="rf-stats__grid">
          <div className="rf-stat" data-reveal="true">
            <div className="rf-stat__num"><span data-count="60" ref={refs.statPoolCount}>0</span>ft</div>
            <div className="rf-stat__label" ref={refs.statPoolLabel}>Longest pool, 6 ft deep</div>
          </div>
          <div className="rf-stat" data-reveal="true">
            <div className="rf-stat__num"><span data-count="24">0</span>hr</div>
            <div className="rf-stat__label">Non-stop generator power</div>
          </div>
          <div className="rf-stat" data-reveal="true">
            <div className="rf-stat__num"><span data-count="4" ref={refs.statRoomsCount}>0</span></div>
            <div className="rf-stat__label" ref={refs.statRoomsLabel}>Non-stop ACs &amp; furnished rooms</div>
          </div>
          <div className="rf-stat" data-reveal="true">
            <div className="rf-stat__num"><span data-count="10">0</span>min</div>
            <div className="rf-stat__label">From Gulshan-e-Maymar</div>
          </div>
        </div>
      </section>

      {/* ===== POOL FEATURE (Gold, animated) ===== */}
      <section id="pool" className="rf-pool" ref={refs.pool}>
        <div className="rf-pool__bubbles rf-pool__bubbles--left" ref={refs.bubbleLeft}></div>
        <div className="rf-pool__bubbles rf-pool__bubbles--right" ref={refs.bubbleRight}></div>
        <div className="rf-pool__grid">
          <div data-reveal="true">
            <div className="rf-eyebrow">Gold · The centrepiece</div>
            <h2>Dive into a<br /><span className="rf-hero__accent">30 × 60 ft</span> pool</h2>
            <p>Covered, floodlit and 6 ft deep — swim by day, glow by night. Loungers, changing rooms and showers right at the water's edge. It's the reason families keep coming back.</p>
            <div className="rf-tags">
              <span className="rf-tag">6 ft deep</span>
              <span className="rf-tag">Covered &amp; floodlit</span>
              <span className="rf-tag">Poolside loungers</span>
            </div>
            <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '5.4s' }}>Book a pool day →</a>
          </div>
          <div className="rf-pool__media" data-reveal="true">
            <img loading="lazy" decoding="async" src="/landing/pool-covered.jpg" alt="Covered swimming pool at night" />
            <div className="rf-pool__shimmer"></div>
            <div className="rf-pool__caption">Riaz Farmhouse Gold · Night swim</div>
          </div>
        </div>
      </section>

      {/* ===== SILVER POOL FEATURE (rain, reversed) ===== */}
      <section id="silver-pool" className="rf-silverpool">
        <div className="rf-silverpool__grid">
          <div className="rf-silverpool__media-wrap" data-reveal="true">
            <div className="rf-silverpool__media">
              <img loading="lazy" decoding="async" src="/landing/silver-pool.jpg" alt="Riaz Farmhouse Silver pool in the rain" />
              <div className="rf-silverpool__rain"></div>
              <div className="rf-silverpool__shimmer"></div>
              <div className="rf-silverpool__caption">Riaz Farmhouse Silver · Rainy-night pool</div>
            </div>
            <div className="rf-silverpool__inset">
              <img loading="lazy" decoding="async" src="/landing/silver-pool-dark.jpg" alt="Silver poolside deck view" />
            </div>
          </div>
          <div className="rf-silverpool__text" data-reveal="true">
            <div className="rf-eyebrow rf-eyebrow--silver">Silver · The centrepiece</div>
            <h2>A <span className="rf-hero__accent">30 × 50 ft</span> pool that shines in the rain</h2>
            <p>Deep dark-tiled water, wide stone deck and poolside loungers under the lights — the Silver pool has the same 6 ft depth and the same wrap-around poolside space as Gold, in a cosier setting for smaller groups.</p>
            <div className="rf-tags">
              <span className="rf-tag rf-tag--silver">30 × 50 ft</span>
              <span className="rf-tag rf-tag--silver">6 ft deep</span>
              <span className="rf-tag rf-tag--silver">Wide stone poolside deck</span>
              <span className="rf-tag rf-tag--silver">Poolside loungers</span>
            </div>
            <a href="#silver" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '5.6s' }}>Book the Silver pool →</a>
          </div>
        </div>
      </section>

      {/* ===== TWO FARMHOUSES ===== */}
      <section id="stays" className="rf-stays">
        <div className="rf-stays__head" data-reveal="true">
          <div className="rf-eyebrow rf-eyebrow--gold-dark">Two farmhouses · one booking form</div>
          <h2>Choose your escape</h2>
          <p>The bigger <strong>Gold</strong> or the cosy <strong>Silver</strong> — both come with a private pool, gardens, games and round-the-clock power. Full facilities and booking charges below, side by side.</p>
        </div>

        <div className="rf-stays__grid">
          {/* Gold card */}
          <div data-reveal="true">
            <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-card rf-card--gold">
              <div className="rf-card__media">
                <img loading="lazy" decoding="async" src="/landing/pool-night.jpg" alt="Farmhouse Gold" />
                <div className="rf-card__scrim"></div>
                <div className="rf-card__tier rf-card__tier--gold">Gold · The Big One</div>
                <div className="rf-card__heading">
                  <h3>Farmhouse Gold</h3>
                  <div className="rf-card__tagline">Biggest pool · 3 bedrooms · up to 4 ACs</div>
                </div>
              </div>
              <div className="rf-card__body">
                <div className="rf-card__specs">
                  <div className="rf-card__spec"><div className="rf-card__spec-v">30×60</div><div className="rf-card__spec-k">Pool (ft)</div></div>
                  <div className="rf-card__spec"><div className="rf-card__spec-v">3</div><div className="rf-card__spec-k">Bedrooms</div></div>
                  <div className="rf-card__spec"><div className="rf-card__spec-v">4</div><div className="rf-card__spec-k">Non-stop ACs</div></div>
                </div>
                <div className="rf-card__facilities">
                  {goldFacilities.map((f) => (
                    <div className="rf-card__facility" key={f}><span className="rf-card__check"><CheckIcon /></span>{f}</div>
                  ))}
                </div>

                <div className="rf-card__pricing" id="pricing">
                  <div className="rf-card__pricing-head">💰 Booking charges</div>
                  <div className="rf-cardprice-group">
                    <div className="rf-card__pricing-sub">10-Hour Slot</div>
                    {pricing.gold.slots10.map((group) => (
                      <div key={group.period}>
                        <div className="rf-cardprice-period">{group.period}</div>
                        {group.rows.map((row) => (
                          <div className="rf-cardprice-row" key={row.label}>
                            <span className="rf-cardprice-row__label">{row.label}</span>
                            <span className="rf-cardprice-row__value">{row.price.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div className="rf-cardprice-note">‼️ Same rate applies on Public Holidays</div>
                  </div>
                  <div className="rf-cardprice-group">
                    <div className="rf-card__pricing-sub">22-Hour Full Slot</div>
                    {pricing.gold.slot22.map((row) => (
                      <div className="rf-cardprice-row" key={row.label}>
                        <span className="rf-cardprice-row__label">{row.label}</span>
                        <span className="rf-cardprice-row__value">{row.price.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rf-cardprice-tags">
                    <span className="rf-cardprice-tag">☀️ Day: 8AM–6PM</span>
                    <span className="rf-cardprice-tag">🌙 Night: 8PM–6AM</span>
                  </div>
                  <div className="rf-cardprice-security">
                    <span className="rf-cardprice-security__label">🔒 Refundable security</span>
                    <span className="rf-cardprice-security__value">{SECURITY_DEPOSIT.toLocaleString()}</span>
                  </div>
                  <p className="rf-cardprice-footnote">Rates are per booking (not per person) — same slot, same price, whether it's 10 guests or 50.</p>
                </div>

                <div className="rf-card__promo">
                  <div><div className="rf-card__promo-label">Use code</div><div className="rf-card__promo-code">RIAZFARMHOUSENOW</div></div>
                  <div className="rf-card__promo-amount"><div className="rf-card__promo-value">10,000 PKR</div><div className="rf-card__promo-off">off at checkout</div></div>
                </div>
                <span className="rf-card__book">Book Farmhouse Gold →</span>
              </div>
            </a>
          </div>

          {/* Silver card */}
          <div data-reveal="true">
            <a href="#silver" target="_blank" rel="noopener noreferrer" className="rf-card rf-card--silver">
              <div className="rf-card__media">
                <img loading="lazy" decoding="async" src="/landing/silver-pool.jpg" alt="Farmhouse Silver" />
                <div className="rf-card__scrim"></div>
                <div className="rf-card__tier rf-card__tier--silver">Silver · The Cosy One</div>
                <div className="rf-card__heading">
                  <h3>Farmhouse Silver</h3>
                  <div className="rf-card__tagline">Private pool · 2 rooms · foosball &amp; games</div>
                </div>
              </div>
              <div className="rf-card__body">
                <div className="rf-card__specs">
                  <div className="rf-card__spec"><div className="rf-card__spec-v">30×50</div><div className="rf-card__spec-k">Pool (ft)</div></div>
                  <div className="rf-card__spec"><div className="rf-card__spec-v">2</div><div className="rf-card__spec-k">Rooms</div></div>
                  <div className="rf-card__spec"><div className="rf-card__spec-v">24h</div><div className="rf-card__spec-k">Generator</div></div>
                </div>
                <div className="rf-card__facilities">
                  {silverFacilities.map((f) => (
                    <div className="rf-card__facility" key={f}><span className="rf-card__check"><CheckIcon /></span>{f}</div>
                  ))}
                </div>

                <div className="rf-card__pricing">
                  <div className="rf-card__pricing-head">💰 Booking charges</div>
                  <div className="rf-cardprice-group">
                    <div className="rf-card__pricing-sub">10-Hour Slot</div>
                    {pricing.silver.slots10.map((group) => (
                      <div key={group.period}>
                        <div className="rf-cardprice-period">{group.period}</div>
                        {group.rows.map((row) => (
                          <div className="rf-cardprice-row" key={row.label}>
                            <span className="rf-cardprice-row__label">{row.label}</span>
                            <span className="rf-cardprice-row__value">{row.price.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="rf-cardprice-group">
                    <div className="rf-card__pricing-sub">22-Hour Full Slot</div>
                    {pricing.silver.slot22.map((row) => (
                      <div className="rf-cardprice-row" key={row.label}>
                        <span className="rf-cardprice-row__label">{row.label}</span>
                        <span className="rf-cardprice-row__value">{row.price.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rf-cardprice-tags">
                    <span className="rf-cardprice-tag">☀️ Day: 8AM–6PM</span>
                    <span className="rf-cardprice-tag">🌙 Night: 8PM–6AM</span>
                  </div>
                  <div className="rf-cardprice-security">
                    <span className="rf-cardprice-security__label">🔒 Refundable security</span>
                    <span className="rf-cardprice-security__value">{SECURITY_DEPOSIT.toLocaleString()}</span>
                  </div>
                  <p className="rf-cardprice-footnote">Rates are per booking (not per person) — same slot, same price, whether it's 10 guests or 30.</p>
                </div>

                <div className="rf-card__promo">
                  <div><div className="rf-card__promo-label">Use code</div><div className="rf-card__promo-code">RIAZSILVERNOW</div></div>
                  <div className="rf-card__promo-amount"><div className="rf-card__promo-value">5,000 PKR</div><div className="rf-card__promo-off">off at checkout</div></div>
                </div>
                <span className="rf-card__book">Book Farmhouse Silver →</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ===== GALLERY ===== */}
      <section id="gallery" className="rf-gallery">
        <div className="rf-gallery__head" data-reveal="true">
          <div className="rf-eyebrow">A look inside</div>
          <h2>Spaces made for<br />gathering</h2>
        </div>

        <div className="rf-gallery__catalog">
          <div className="rf-gallery__catalog-head rf-gallery__catalog-head--gold">🥇 Riaz Farmhouse Gold</div>
          {goldGalleryGroups.map((group) => (
            <div className="rf-gallery__group" key={group.label}>
              <div className="rf-gallery__group-label">{group.label}</div>
              <div className="rf-gallery__grid">
                {group.items.map((item) => (
                  <div className="rf-gallery__item" data-reveal="true" key={item.src}>
                    <img loading="lazy" decoding="async" src={item.src} alt={item.alt} />
                    <div className="rf-gallery__scrim"></div>
                    <div className="rf-gallery__caption">{item.caption}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="rf-gallery__catalog">
          <div className="rf-gallery__catalog-head rf-gallery__catalog-head--silver">🥈 Riaz Farmhouse Silver</div>
          {silverGalleryGroups.map((group) => (
            <div className="rf-gallery__group" key={group.label}>
              <div className="rf-gallery__group-label">{group.label}</div>
              <div className="rf-gallery__grid">
                {group.items.map((item) => (
                  <div className="rf-gallery__item" data-reveal="true" key={item.src}>
                    <img loading="lazy" decoding="async" src={item.src} alt={item.alt} />
                    <div className="rf-gallery__scrim"></div>
                    <div className="rf-gallery__caption">{item.caption}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SILVER WALK-THROUGH VIDEO — video left, text + big CTA right,
          same layout and deferred-load pattern as the Reviews video below ===== */}
      <section id="walkthrough" className="rf-walkthrough" ref={refs.walkthroughSection}>
        <div className="rf-walkthrough__grid">
          <div data-reveal="true">
            <div className="rf-video-card">
              <video
                ref={refs.walkthroughVideo}
                data-src="/landing/silver/silver-walkthrough.mp4"
                poster="/landing/silver/silver-walkthrough-poster.jpg"
                muted
                loop
                playsInline
                preload="none"
              ></video>
              <div className="rf-video-card__spinner" ref={refs.walkthroughSpinner}><span></span></div>
              <div className="rf-video-card__scrim"></div>
              <div className="rf-video-card__live"><span className="rf-video-card__live-dot"></span>WALK-THROUGH</div>
              <div className="rf-video-card__info">
                <div className="rf-video-card__name">Riaz Farmhouse Silver</div>
                <div className="rf-video-card__meta">Full property tour</div>
              </div>
              <button className="rf-video-card__mute" ref={refs.walkthroughMuteBtn} aria-label="Toggle sound">
                <span ref={refs.walkthroughMuteIcon} style={{ display: 'flex' }} dangerouslySetInnerHTML={{ __html: ICON_MUTED }} />
              </button>
            </div>
          </div>
          <div data-reveal="true">
            <div className="rf-eyebrow rf-eyebrow--silver">🥈 Riaz Farmhouse Silver</div>
            <h2>Take the full<br />walk-through</h2>
            <p>See the whole Silver farmhouse before you book — pool, rooms and lawn, one continuous tour.</p>
            <a href="#silver" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold rf-btn--big" style={{ '--sheen-dur': '5.3s' }}>Book Silver Now →</a>
          </div>
        </div>
      </section>

      {/* ===== REVIEWS: video + auto-switching floating cards ===== */}
      <section id="reviews" className="rf-reviews" ref={refs.reviewsSection}>
        <div className="rf-reviews__grid">

          {/* Featured video review */}
          <div data-reveal="true">
            <div className="rf-eyebrow">Featured guest review</div>
            <div className="rf-video-card">
              <video ref={refs.video} data-src="/landing/review-video.mp4" poster="/landing/review-poster.jpg" muted loop playsInline preload="none"></video>
              <div className="rf-video-card__spinner" ref={refs.vidSpinner}><span></span></div>
              <div className="rf-video-card__scrim"></div>
              <div className="rf-video-card__live"><span className="rf-video-card__live-dot"></span>REVIEW</div>
              <div className="rf-video-card__info">
                <div className="rf-video-card__name">Jumma Gujjar</div>
                <div className="rf-video-card__meta">Jumma Gujjar Nihari, Karachi</div>
                <div className="rf-video-card__stars">★★★★★</div>
              </div>
              <button className="rf-video-card__mute" ref={refs.muteBtn} aria-label="Toggle sound">
                <span ref={refs.muteIcon} style={{ display: 'flex' }} dangerouslySetInnerHTML={{ __html: ICON_MUTED }} />
              </button>
            </div>
            <div className="rf-reviews__video-note">Tap the sound icon to hear what our guest has to say about Riaz Farmhouse Gold.</div>
          </div>

          {/* Auto-switching text reviews */}
          <div data-reveal="true">
            <div className="rf-reviews__head">
              <h2>Loved by<br />our guests</h2>
              <div className="rf-reviews__rating">
                <div className="rf-reviews__rating-num">4.9</div>
                <div className="rf-reviews__rating-stars">★★★★★</div>
                <div className="rf-reviews__rating-src">Google reviews</div>
              </div>
            </div>

            <div className="rf-review-stack" ref={refs.reviewStack}>
              {reviews.map((r) => (
                <div className="rf-review-card" data-review-card="true" key={r.name}>
                  <div className="rf-review-card__inner">
                    <div className="rf-review-card__stars">★★★★★</div>
                    <p className="rf-review-card__quote">"{r.quote}"</p>
                    <div className="rf-review-card__by">
                      <div className="rf-review-card__avatar">{r.initial}</div>
                      <div><div className="rf-review-card__name">{r.name}</div><div className="rf-review-card__meta">Google review · 5★</div></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rf-review-dots" ref={refs.reviewDots}>
              {reviews.map((r, i) => (
                <button className="rf-review-dot" data-review-dot="true" aria-label={`Show review ${i + 1}`} key={r.name}></button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== LOCATION / BIG CTA ===== */}
      <section id="location" className="rf-location">
        <div className="rf-location__card" data-reveal="true">
          <div className="rf-location__bg"></div>
          <div className="rf-location__content">
            <div className="rf-eyebrow">Located in Dua Greens, Karachi</div>
            <h2>Just <span className="rf-hero__accent">10 minutes</span> from Gulshan-e-Maymar</h2>
            <p>Easy to reach, far enough to feel away. Plan your vacation, gather your people and lock your slot before it's gone — weekends fill fast.</p>
            <div className="rf-location__ctas">
              <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '5s' }}>Book your slot now →</a>
              <a href="https://maps.app.goo.gl/VfdjmU5gJYnH6VSA6?g_st=ac" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--ghost-mint">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5fd0b0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                View on Google Maps
              </a>
              <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--ghost-cream">DM to book a slot</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="rf-footer">
        <div className="rf-footer__grid">
          <div>
            <div className="rf-footer__brand-row">
              <img loading="lazy" decoding="async" src="/logo.png" alt="Riaz Farmhouse" className="rf-footer__logo" />
              <span className="rf-footer__wordmark">Riaz Farmhouse</span>
            </div>
            <p className="rf-footer__blurb">Karachi's best farmhouse getaway for friends &amp; family. Chill environment guaranteed.</p>
          </div>
          <div>
            <div className="rf-footer__heading">Explore</div>
            <div className="rf-footer__links">
              <a href="#stays">Farmhouses</a>
              <a href="#pricing">Pricing</a>
              <a href="#pool">The Pool</a>
              <a href="#gallery">Gallery</a>
              <a href="#walkthrough">Walk-Through</a>
              <a href="#reviews">Reviews</a>
              <a href="#location">Location</a>
            </div>
          </div>
          <div>
            <div className="rf-footer__heading">Offers</div>
            <div className="rf-footer__offers">
              <div><strong>RIAZFARMHOUSENOW</strong> — 10,000 PKR off Gold</div>
              <div><strong>RIAZSILVERNOW</strong> — 5,000 PKR off Silver</div>
            </div>
          </div>
          <div>
            <div className="rf-footer__heading">Follow us</div>
            <div className="rf-footer__social">
              <div className="rf-footer__social-row">
                <span className="rf-footer__social-tier rf-footer__social-tier--gold">Gold</span>
                <a href="https://www.facebook.com/share/1AuxNWH8nD/" target="_blank" rel="noopener noreferrer" aria-label="Riaz Farmhouse Gold on Facebook" className="rf-footer__icon rf-footer__icon--fb">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94Z"/></svg>
                </a>
                <a href="https://www.instagram.com/riazfarmhouse/" target="_blank" rel="noopener noreferrer" aria-label="Riaz Farmhouse Gold on Instagram" className="rf-footer__icon rf-footer__icon--ig">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16Zm0 1.62c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.17.4-.55.21-.94.47-1.35.88-.41.41-.67.8-.88 1.35-.16.41-.35 1.03-.4 2.17-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.14.24 1.76.4 2.17.21.55.47.94.88 1.35.41.41.8.67 1.35.88.41.16 1.03.35 2.17.4 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.17-.4.55-.21.94-.47 1.35-.88.41-.41.67-.8.88-1.35.16-.41.35-1.03.4-2.17.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.14-.24-1.76-.4-2.17a3.6 3.6 0 0 0-.88-1.35 3.6 3.6 0 0 0-1.35-.88c-.41-.16-1.03-.35-2.17-.4-1.24-.06-1.59-.07-4.74-.07Zm0 2.76a5.3 5.3 0 1 1 0 10.6 5.3 5.3 0 0 1 0-10.6Zm0 8.74a3.44 3.44 0 1 0 0-6.88 3.44 3.44 0 0 0 0 6.88Zm6.74-8.95a1.24 1.24 0 1 1-2.48 0 1.24 1.24 0 0 1 2.48 0Z"/></svg>
                </a>
              </div>
              <div className="rf-footer__social-row">
                <span className="rf-footer__social-tier rf-footer__social-tier--silver">Silver</span>
                <a href="https://www.facebook.com/share/17tH8Q9Kto/" target="_blank" rel="noopener noreferrer" aria-label="Riaz Farmhouse Silver on Facebook" className="rf-footer__icon rf-footer__icon--fb">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94Z"/></svg>
                </a>
                <a href="https://www.instagram.com/riazfarmhousesilver/" target="_blank" rel="noopener noreferrer" aria-label="Riaz Farmhouse Silver on Instagram" className="rf-footer__icon rf-footer__icon--ig">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16Zm0 1.62c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.17.4-.55.21-.94.47-1.35.88-.41.41-.67.8-.88 1.35-.16.41-.35 1.03-.4 2.17-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.14.24 1.76.4 2.17.21.55.47.94.88 1.35.41.41.8.67 1.35.88.41.16 1.03.35 2.17.4 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.17-.4.55-.21.94-.47 1.35-.88.41-.41.67-.8.88-1.35.16-.41.35-1.03.4-2.17.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.14-.24-1.76-.4-2.17a3.6 3.6 0 0 0-.88-1.35 3.6 3.6 0 0 0-1.35-.88c-.41-.16-1.03-.35-2.17-.4-1.24-.06-1.59-.07-4.74-.07Zm0 2.76a5.3 5.3 0 1 1 0 10.6 5.3 5.3 0 0 1 0-10.6Zm0 8.74a3.44 3.44 0 1 0 0-6.88 3.44 3.44 0 0 0 0 6.88Zm6.74-8.95a1.24 1.24 0 1 1-2.48 0 1.24 1.24 0 0 1 2.48 0Z"/></svg>
                </a>
              </div>
              <a href="https://maps.app.goo.gl/VfdjmU5gJYnH6VSA6?g_st=ac" target="_blank" rel="noopener noreferrer" className="rf-footer__maps">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#5fd0b0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                Find us on Google Maps
              </a>
            </div>
          </div>
          <div className="rf-footer__cta-col">
            <a href="#gold" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--gold" style={{ '--sheen-dur': '5.7s' }}>Book Now →</a>
            <a href="http://wa.me/+923392959290" target="_blank" rel="noopener noreferrer" className="rf-btn rf-btn--whatsapp">
              <span className="rf-btn__wa-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20.52 3.48C18.39 1.35 15.38 0 12 0 5.37 0 0 5.37 0 12c0 2.1.55 4.15 1.59 5.95L0 24l6.27-1.64A11.92 11.92 0 0012 24c6.63 0 12-5.37 12-12 0-3.38-1.35-6.39-3.48-8.52z" fill="#25D366"/><path d="M17.55 14.63c-.29-.14-1.73-.86-2-.96-.27-.1-.47-.14-.67.14-.19.29-.74.96-.91 1.16-.17.19-.33.21-.62.07-.29-.14-1.22-.45-2.33-1.44-.86-.76-1.44-1.7-1.61-1.99-.17-.29-.02-.44.12-.58.12-.12.27-.32.4-.48.13-.16.17-.28.26-.46.09-.19.05-.35-.02-.49-.07-.14-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.19 0-.5.07-.77.35-.27.28-1.03 1.01-1.03 2.47s1.06 2.86 1.21 3.06c.14.19 2.09 3.18 5.07 4.46.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.09 1.73-.7 1.98-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.19-.57-.33z" fill="#FFF"/></svg>
              </span>
              <span className="rf-btn__wa-label">Chat on WhatsApp</span>
            </a>
          </div>
        </div>
        <div className="rf-footer__bottom">
          <span>© 2026 Riaz Farmhouse · Located in Dua Greens, Karachi</span>
          <span>Book your slot · Endless fun 🤩</span>
        </div>
      </footer>

    </div>
  );
}

export default memo(LandingPage);
